<template>
    <div :class="['course-addon',
        {
            'not-logged' : !isLoggedIn,
            'no-background' : noBackGround,
            'no-margins': noMargins,
            'no-table-of-contents': noTableOfContents
        }]">
        <div class="course-addon__container">
            <span
                class="course-addon__completion-text"
                v-html="$t('CourseTest.course_completion')"
            >
            </span>
            <CourseProgressBar
                v-if="shouldShowProgressBar"
                :progress="getCurrentWatchedPercent"
                :isCompleted="courseCompleted"
            />
            <template v-if="showConfirmCourseTestButton">
                <TotemButton
                    class="course-addon__button"
                    v-on:click="confirmCourseCompleted"
                    color="primary"
                    :disabled="disableConfirmCourseButton || isVideoDemo"
                    :size="size"
                    :label="$t('CourseTest.confirm')"
                />
                <template v-if="showDescriptionText">
                    <span
                        class="course-addon__info"
                        v-html="$t(getConfirmationTextKey)">
                    </span>
                </template>
            </template>
            <template v-if="!courseCompleted && hasTest">
                <TotemButton
                    class="course-addon__button"
                    v-on:click="showComaTest"
                    color="primary"
                    :disabled="disableCourseTest || isVideoDemo"
                    :size="size"
                    :label="$t('CourseTest.take_test')"
                />
                <template v-if="showDescriptionText">
                    <span
                        class="course-addon__info"
                        v-html="$t(getCourseTestInfoKey)">
                    </span>
                </template>
            </template>
            <template v-if="courseCompleted">
                <TotemButton
                    class="course-addon__button"
                    v-on:click="createCertificate"
                    color="secondary"
                    icon="certificate"
                    :disabled="!isLoggedIn || isVideoDemo"
                    :size="size"
                    :label="$t('CourseTest.load_certificate')"
                />
                <span v-if="showDescriptionText" class="course-addon__info-completed"><span v-html="$t('CourseTest.you_have_completed_course')"></span></span>
            </template>
            <ModalTestCourse :testUrl="testUrl"/>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import convertMinutesToHours from 'components/shared/mixins/convertMinutesToHours'
    import ModalTestCourse from 'components/shared/modals/ModalTestCourse'
    import Progress from 'easy-circular-progress'
    import dateHelper from 'components/shared/mixins/dateHelper'
    import moment from 'moment'
    import CourseProgressBar from 'components/coursepage/courseaddons/CourseProgressBar'


    export default {
        components: { ModalTestCourse, Progress, CourseProgressBar },
        props: {
            title: String,
            courseId: [ String, Number ],
            canCourseTestBeOpen: {
                type: Boolean,
                default: false
            },
            courseWithoutTestCompleted: {
                type: Boolean,
                default: false
            },
            hasTest: {
                type: Boolean,
                default: false
            },
            isMicroLearningCourse: {
                type: Boolean,
                default: false
            },
            isDemo: Boolean,
            isDigitalClassRoomTraining: Boolean,
            isLiveWebinar: Boolean,
            isUpcoming: Boolean,
            isTestOnly: Boolean,
            showDescriptionText: {
                type: Boolean,
                default: true
            },
            noBackGround: {
                type: Boolean,
                default: false
            },
            noMargins: {
                type: Boolean,
                default: false
            },
            size: {
                type: String,
                default: 'large'
            },
            singleLearningPath: Object,
            noTableOfContents: {
                type: Boolean,
                default: false
            }
        },
        mixins: [ convertMinutesToHours, dateHelper ],
        computed: {
            ...mapGetters({
                getCurrentUser: 'User/getCurrentUser',
                isLoggedIn: 'isLoggedIn',
                getCourseById: 'Courses/getCourseById',
                getCertificateForCourse: 'CompletedCourses/getCertificateForCourse',
                course: 'Courses/getSingle',
                canMicroLearningTestBeOpen: 'MicroLearning/canMicroLearningTestBeOpen',
                microLearningCurrentWatchedPercent: 'MicroLearning/getMicroLearningCurrentWatchedPercent',
                digitalClassroomSubscriptions: 'Courses/getSignedUpEventsForUser',
                signedUpEvents: 'Courses/getSignedUpEventsForUser',
                singleCourseWatchedPercent: 'VideoPlayer/getWatchedTotalPercentage'
            }),
            getCourseTestInfoKey() {
                if (this.isMicroLearningCourse) return 'CourseTest.microlearning_coursetest_info';
                if (this.isDigitalClassRoomTraining) return 'CourseTest.digital_classroom_coursetest_info';
                return 'CourseTest.here_take_test_bla_bla';
            },
            getConfirmationTextKey () {
                if (this.isTestOnly) return 'CourseTest.no_test_testonly';
                if (this.isMicroLearningCourse) return 'CourseTest.no_test_microlearning';
                if (this.isDigitalClassRoomTraining) return 'CourseTest.no_test_digital_classroom';
                if (this.isLiveWebinar) return 'CourseTest.no_test_live_event';
                return 'CourseTest.no_test';
            },
            isDigitalClassroomSignupValid () {
                if (!this.isDigitalClassRoomTraining || !this.isLoggedIn || !this.digitalClassroomSubscriptions) {
                    return false
                }
                const subscriptions = this.digitalClassroomSubscriptions.filter(
                    subscription => subscription.training?.course.id === Number(this.courseId) && subscription?.subscriptionStatus === 'ok'
                )
                if (!subscriptions.length === 0) {
                    return false
                }

                const now = moment().unix()
                const endedTrainings = subscriptions.filter(subscription => {
                    const sessions = subscription.training.sessions
                    return now > moment(sessions[sessions.length - 1].endDateAndTime).unix()
                })

                return endedTrainings.length > 0
            },
            courseCompleted () {
                return this.getCertificateForCourse(this.courseId)
            },
            courseWithoutTest () {
                return !this.hasTest && !this.canCourseTestBeOpen
            },
            disableCourseTest () {
                if (this.isTestPage) {
                    return false
                }
                if (this.isDigitalClassRoomTraining && this.isDigitalClassroomSignupValid) {
                    return false
                }
                if (this.isMicroLearningCourse && this.isLoggedIn) {
                    return !this.canMicroLearningTestBeOpen
                }
                return !this.isLoggedIn || !this.canCourseTestBeOpen
            },
            showConfirmCourseTestButton () {
                return (!this.hasTest && !this.courseCompleted) ||
                    (!this.hasTest && this.isMicroLearningCourse && !this.courseCompleted)
            },
            disableConfirmCourseButton () {
                if (this.isTestOnly && !this.hasTest) {
                    return true
                }
                if (this.isTestPage) {
                    return false
                }
                if (this.isDigitalClassRoomTraining && this.isDigitalClassroomSignupValid) {
                    return false
                }
                if (this.isUpcoming) {
                    return true
                }
                if (this.isDigitalClassRoomTraining && this.isPastSignUpForThisEvent) {
                    return false
                }
                if (this.isMicroLearningCourse && this.isLoggedIn) {
                    return !this.canMicroLearningTestBeOpen
                }
                return !this.courseWithoutTestCompleted || !this.isLoggedIn || this.disableConfirmButton
            },
            shouldShowProgressBar () {
                if (this.isTestPage || this.isDigitalClassRoomTraining) {
                    return false
                }
                return true
            },
            isTestPage () {
                if (this.isTestOnly) {
                    return true
                }
                if (this.course.videoDuration === undefined) {
                    return false
                }
                return this.course.videoDuration === 1 && this.course.type.value !== 'virtual_classroom'
            },
            isVideoDemo () {
                return this.isDemo && !this.isDigitalClassRoomTraining
            },
            isPastEventTime () {
                const { eventTime, eventDate } = this.course.enroll
                return moment(`${eventDate} ${eventTime}`).unix() < +this.currentTime
            },
            isPastSignUpForThisEvent () {
                let pastSignUps = this.signedUpEvents.filter(event => {
                    const sessions = event.training.sessions
                    return this.course.id === event.training.course.id && moment().unix() > moment(sessions[sessions.length - 1].endDateAndTime).unix()
                })
                return pastSignUps.length > 0
            },
            getCurrentWatchedPercent () {
                if (this.isMicroLearningCourse) {
                    return this.microLearningCurrentWatchedPercent
                }
                if (!this.course.videoDuration) {
                    return 0
                }
                return this.singleCourseWatchedPercent
            },
        },
        methods: {
            async showComaTest () {
                try {
                    const { url } = await this.$store.dispatch('CompletedCourses/loadCourseTestUrl', this.courseId)
                    this.testUrl = url
                    this.$modal.show('coma-test')
                    this.consumed = true
                } catch (e) {
                    this.$modal.show('dialog', {
                        text: this.$t('CourseTest.fail_to_open')
                    })
                }
            },
            createCertificate () {
                if (this.courseCompleted) {
                    this.$store.dispatch('Certificates/createCertificate', this.getCertificateForCourse(this.courseId))
                }
            },
            async confirmCourseCompleted () {
                this.disableConfirmButton = true
                await this.$store.dispatch('CompletedCourses/addNoQuizCertificate', this.courseId)
                if (this.singleLearningPath !== null && this.singleLearningPath) {
                    this.$store.dispatch('CompletedCourses/setReloadLearningPathsState', true)
                    this.$store.dispatch('LearningPaths/createLearningPathCertificate', this.singleLearningPath.id)
                }
                await Promise.all([this.$store.dispatch('CompletedCourses/loadCertificates')])
            },
            updateCurrentTime () {
                this.currentTime = this.getUnixTimeStampByTimeZone()
            },
            getSubscriptionEndingTime (subscribedEvent) {
                return moment(subscribedEvent.training.sessions[0].endDateAndTime).unix()
            }
        },
        data () {
            return {
                consumed: false,
                testUrl: '',
                disableConfirmButton: false,
                currentTime: this.getUnixTimeStampByTimeZone(),
                currentTimeInterval: null
            }
        },
        mounted () {
            this.currentTime = this.getUnixTimeStampByTimeZone()
            this.currentTimeInterval = setInterval(this.updateCurrentTime, 60000)
        },
        beforeDestroy () {
            clearInterval(this.currentTimeInterval)
        },
        watch: {
            singleLearningPath () {
                if (this.singleLearningPath.canUserCreateCertificate && !this.singleLearningPath.hasUserCertificate) {
                    this.$store.dispatch('LearningPaths/createLearningPathCertificate', this.singleLearningPath.id)
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .course-addon {
        display: flex;
        flex-direction: column;
        position: relative;
        color: $black;
        background: $white;
        padding: 10px;
        font-family: PlusJakartaSans-Regular, sans-serif;
        margin-bottom: 30px;
        overflow-y: auto;
        overflow-x: hidden;
        width: 300px;
        border: solid 2px $light_blue;
        border-top: none;
        border-radius: 0 0 30px 30px;

        &__container {
            background: $concrete;
            border-radius: 20px;
            padding: 20px 10px;
        }

        &.no-table-of-contents {
        }

        span[class*="icon-"] {
            font-size: 2.4rem;
         }

        [class*="desktop"] & {
            max-width: 370px;
        }
        [class*="desktop_xxxl"] & {
            width: 370px;
        }
        [class*="mobile"] & {
            width: 100%;
        }

        &__title {
            font-size: 1.8rem;
        }

        &__button {
            align-self: center;
            margin: 10px 0;
            padding: 0 !important;
            width: 100% !important;
        }

        &__test-info {
            display: flex;
            justify-content: center;
            text-align: center;
        }

        &__info {
            color: $black;
            font-size: 1.4rem;
            text-align: center;
        }

        &__info-completed {
            color: $tuatara;
            font-size: 1.4rem;
            font-family: PlusJakartaSans-Regular, sans-serif;
        }

        .icon-check-square {
            font-size: 1.3rem !important;
            color: $ice-cold;
            padding: 5px;
        }

        &__completion-text {
            font-family: PlusJakartaSans-Bold, sans-serif;
            font-size: 1.6rem;
        }
    }
</style>
